import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    DataTransferOperations,
    DataTransferProgressStatus,
    DataTransferState,
    isDataTransferStateActive
} from "./types";
import { RootState } from "@redux/hooks";
import { Integration } from "@/types/general";

const initialState = {
    operations: {} as Partial<Record<DataTransferOperations, DataTransferState>>
};

export const dfsSlice = createSlice({
    name: "dfs",
    initialState: initialState,
    reducers: {
        startOperation: (state, action: PayloadAction<{
            operation: DataTransferOperations,
            module: string,
            entities?: Integration.Import.Entity[]
        }>) => {
            const { operation, module } = action.payload;
            const previousState = state.operations[operation];

            // The DataTransferStarted socket event and the `data-transfer/current` bootstrap re-announce operations this client has already started.
            // They don't know the entities, so we keep the ones registered by the initiator.
            const entities =
                action.payload.entities ??
                (isDataTransferStateActive(previousState) && previousState.module === module
                    ? previousState.entities
                    : undefined);

            state.operations[operation] = {
                operation,
                status: DataTransferProgressStatus.ACTIVE,
                module,
                entities
            };
        },

        updateOperationProgress: (state, action: PayloadAction<{
            operation: DataTransferOperations,
            current: number[],
            total: number[],
            errors?: number
        }>) => {
            const operationState = state.operations[action.payload.operation];

            if (operationState?.status === DataTransferProgressStatus.ACTIVE) {
                const { current, total, errors } = action.payload;

                state.operations[action.payload.operation] = {
                    ...operationState,
                    progress: current.map((c, i) => ({
                        current: c,
                        total: total[i]
                    })),
                    errors
                };
            }
        },

        finishOperation: (state, action: PayloadAction<{
            operation: DataTransferOperations,
            has_errors?: boolean,
            data?: any
        }>) => {
            const operation = action.payload.operation;
            const operationState = state.operations[operation];

            if (operationState) {
                state.operations[action.payload.operation] = {
                    operation: operation,
                    status: DataTransferProgressStatus.FINISHED,
                    module: operationState.module,
                    entities: operationState.entities,
                    has_errors: action.payload.has_errors ?? false,
                    errors: isDataTransferStateActive(operationState) ? operationState.errors : undefined,
                    data: action.payload.data
                };
            } else {
                console.warn(`Unable to "finish" the operation [${operation}] because it was not started (not registered in the state)`);
            }
        },

        resetOperation: (state, action: PayloadAction<{ operation: DataTransferOperations }>) => {
            delete state.operations[action.payload.operation];
        }
    }
});

export const selectOperation = (operation: DataTransferOperations) => (state: RootState) => state.dfs.operations[operation];

/**
 * The backend runs a single data transfer (import, export, sync, etc.) per company at a time.
 */
export const selectIsAnyOperationActive = (state: RootState) =>
    Object.values(state.dfs.operations).some(isDataTransferStateActive);

// Action creators are generated for each case reducer function
export const {
    startOperation,
    updateOperationProgress,
    finishOperation,
    resetOperation
} = dfsSlice.actions;

export default dfsSlice.reducer;
